import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { DataGrid } from "@material-ui/data-grid";
import { Button, Grid, Chip, Tooltip, TextField } from "@material-ui/core";
import DownloadIcon from "@material-ui/icons/GetApp";
import { CSVLink } from "react-csv";
import Snackbar from "@material-ui/core/Snackbar";
import { Alert } from "@material-ui/lab";
import PaperedInput from "../../../components/Inputs/PaperedInput";
import { debounce } from "../../../utils/functions";
import DashboardTable from "../../../containers/DashboardTable";
import UpsellModal from "./UpsellModal";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import upsellApi from "../../../api/upsell-api";
import cinemasApi from "../../../api/cinemas";
import emptyConcessions from "../../../assets/images/empty-concessions.png";
import UpsellDeleteModal from "./UpsellDeleteModal";
import { CircularLoad } from "../../../components/Loader";
import UpsellProductModal from "./UpsellProductModal";
import DateSelector from "../../../components/Inputs/DateSelector";
import moment from "moment";

const UpsellActionsMenu = ({
  upsell,
  setIsUpsellOpen,
  setUpsellMode,
  setUpsellToEdit,
  setUpsellToDelete,
  setDeleteModalOpen
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = event => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    setUpsellMode("edit");
    setUpsellToEdit(upsell);
    setIsUpsellOpen(true);

    console.log("Editing upsell:", upsell);
    handleMenuClose();
  };

  const handleDelete = () => {
    setUpsellToDelete(upsell);
    setDeleteModalOpen(true);

    console.log("Deleting upsell:", upsell);
    handleMenuClose();
  };

  return (
    <>
      <button onClick={handleMenuOpen} className="upsell-actions-button">
        <MoreVertIcon />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        className="upsell-actions-menu"
      >
        <MenuItem onClick={handleEdit} className="upsell-actions-menu-item">
          <EditIcon className="edit-icon" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} className="upsell-actions-menu-item">
          <DeleteIcon className="delete-icon" />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

const UpsellManagement = ({ jwt }) => {
  const [upsellProductModalOpen, setUpsellProductModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const [upsellToEdit, setUpsellToEdit] = useState({});
  const [upsellToDelete, setUpsellToDelete] = useState({});
  const [upsellMode, setUpsellMode] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [error, setError] = useState({
    isError: false,
    message: ""
  });
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [cinemasLoading, setCinemasLoading] = useState(false);
  const [selectedState, setSelectedState] = useState({});
  const [selectedCinema, setSelectedCinema] = useState({});
  const [states, setStates] = useState([]);
  const [selectedUpsellProduct, setSelectedUpsellProduct] = useState(null);

  const [analyticsStartDate, setAnalyticsStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [analyticsEndDate, setAnalyticsEndDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [analyticsData, setAnalyticsData] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const formatDate = dateStr => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB");
  };

  const formatTime = timeStr => {
    if (!timeStr) return "";
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds || 0, 0);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const AlertSnackbar = props => {
    return <Alert elevation={6} variant="filled" {...props} />;
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    autoHideDuration: 0,
    severity: "success"
  });

  useEffect(() => {
    setAnalyticsLoading(true);
    setError({ isError: false, message: "" });
    if (!selectedCinema?.id) {
      setLoading(false);
      return;
    }
    upsellApi
      .getUpsellAnalytics(
        `Bearer ${jwt}`,
        selectedCinema.id,
        analyticsStartDate,
        analyticsEndDate
      )
      .then(res => {
        const { meta = {} } = res?.data?.data;
        setAnalyticsLoading(false);

        setAnalyticsData(meta);
      })
      .catch(e => {
        console.log(e);
        setError({ isError: true, message: "There is an error" });
        setAnalyticsLoading(false);
      });
  }, [
    analyticsEndDate,
    analyticsStartDate,
    jwt,
    selectedCinema.id,
    refreshCounter
  ]);

  useEffect(() => {
    setCinemasLoading(true);
    cinemasApi.fetchCinemas(jwt).then(res => {
      setStates(res.data.data);
      setSelectedState(res.data.data[2]);
      setSelectedCinema(res.data.data[2].cinemas[1]);
      setCinemasLoading(false);
    });
  }, [jwt]);

  useEffect(() => {
    setLoading(true);
    setError({ isError: false, message: "" });
    if (!selectedCinema?.id) {
      setLoading(false);
      return;
    }
    upsellApi
      .getUpsells(`Bearer ${jwt}`, selectedCinema.id, page + 1, searchInput)
      .then(res => {
        const { mappings = [], meta = {} } = res?.data?.data;
        setLoading(false);

        const mappedData = mappings.map(item => {
          const concessions = Array.isArray(item.concessions)
            ? item.concessions
            : [];

          const totals = concessions.reduce(
            (acc, item) => {
              acc.clicks += item.clicks;
              acc.purchases += item.purchases;
              return acc;
            },
            { clicks: 0, purchases: 0 }
          );

          return {
            id: item.id,
            cinemaId: selectedCinema.id,
            selectedState: selectedState,
            selectedCinema: selectedCinema,
            movie: {
              id: item.rosetta_movie_id,
              name: item.movieDetails?.movie_name || "Unknown",
              image:
                item.movieDetails?.big_poster ||
                item.movieDetails?.small_poster ||
                item.movieDetails?.small_background ||
                item.movieDetails?.movie_poster
            },
            concessions: concessions.map(c => ({
              id: c.concessionDetails?.product_id || c.rosetta_concession_id,
              name: c.concessionDetails?.product_name || "Unknown",
              image: c.concessionDetails?.image ? (
                <img
                  src={c.concessionDetails.image}
                  alt={c.concessionDetails.product_name}
                  style={{ width: 24, height: 24, objectFit: "cover" }}
                />
              ) : (
                <img
                  src={emptyConcessions}
                  alt={c.concessionDetails?.product_name || "Unknown"}
                  style={{ width: 24, height: 24, objectFit: "cover" }}
                />
              ),
              clicks: c.clicks || 0,
              purchases: c.purchases || 0
            })),

            totalClicks: totals.clicks || 0,
            totalPurchases: totals.purchases || 0,
            startDate: formatDate(item.start_date),
            endDate: formatDate(item.end_date),
            startTime: formatTime(item.start_time),
            endTime: formatTime(item.end_time),
            suggestionMode: item.suggestion_mode
          };
        });

        setData(mappedData);
        setTotalData(meta.totalCount);
      })
      .catch(e => {
        console.log(e);
        setError({ isError: true, message: "There is an error" });
        setLoading(false);
      });
  }, [
    page,
    jwt,
    searchInput,
    refreshCounter,
    selectedCinema.id,
    selectedState.id,
    selectedCinema,
    selectedState
  ]);

  const columns = [
    {
      field: "movie",
      headerName: "Movie",
      width: 300,
      renderCell: params => (
        <div
          style={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}
        >
          <img
            src={params.value.image}
            alt={params.value.name}
            style={{
              width: 50,
              height: 50,
              marginRight: 12,
              objectFit: "cover"
            }}
          />
          <span style={{ fontWeight: "400" }}>{params.value.name}</span>
        </div>
      )
    },
    {
      field: "concessions",
      headerName: "Upsell Products",
      width: 300,
      renderCell: params => {
        const values = params.value || [];
        const hasOverflow = values.length > 2;

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 6,
              alignItems: "center",
              maxWidth: 300,
              height: 72,
              overflow: "hidden"
            }}
          >
            {values.slice(0, 2).map((c, i) => (
              <Tooltip key={i} title={c.name}>
                <Chip
                  label={c.name}
                  icon={c.image}
                  size="small"
                  style={{
                    ...(i === 0
                      ? {
                          backgroundColor: "transparent"
                        }
                      : {
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          maxWidth: 140,
                          backgroundColor: "transparent"
                        })
                  }}
                />
              </Tooltip>
            ))}

            {hasOverflow && (
              <Chip
                label={`+${values.length - 2}`}
                size="small"
                style={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontWeight: "bold"
                }}
              />
            )}
          </div>
        );
      }
    },
    {
      field: "totalClicks",
      headerName: "Clicks",
      width: 120,
      renderHeader: params => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}
        >
          <span style={{ fontWeight: "500" }}>{params.colDef.headerName}</span>
        </div>
      ),
      renderCell: params => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            }}
          >
            <span style={{ fontWeight: "400" }}>{params.value}</span>
          </div>
        );
      }
    },
    {
      field: "totalPurchases",
      headerName: "Purchased",
      width: 140,
      renderHeader: params => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%"
          }}
        >
          <span style={{ fontWeight: "500" }}>{params.colDef.headerName}</span>
        </div>
      ),
      renderCell: params => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            }}
          >
            <span style={{ fontWeight: "400" }}>{params.value}</span>
          </div>
        );
      }
    },
    { field: "startDate", headerName: "Start Date", width: 120 },
    { field: "endDate", headerName: "End Date", width: 120 },
    { field: "startTime", headerName: "Start Time", width: 120 },
    { field: "endTime", headerName: "End Time", width: 120 },
    {
      field: "",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: params => {
        return (
          <UpsellActionsMenu
            upsell={params.row}
            setIsUpsellOpen={setIsUpsellOpen}
            setDeleteModalOpen={setDeleteModalOpen}
            setUpsellMode={setUpsellMode}
            setUpsellToEdit={setUpsellToEdit}
            setUpsellToDelete={setUpsellToDelete}
          />
        );
      }
    }
  ];

  const pollForUpdate = (attempt = 0) => {
    if (attempt >= 1) return;

    setTimeout(() => {
      setRefreshCounter(prev => prev + 1);
      pollForUpdate(attempt + 1);
    }, 1000);
  };

  const handleUpsellConfirm = (payload, mode) => {
    setLoading(true);
    const apiCall =
      mode === "manual"
        ? upsellApi.createManualUpsell(jwt, payload)
        : upsellApi.createSmartUpsell(jwt, payload);

    apiCall
      .then(res => {
        console.log(res);
        console.log("Status:", res.data.status);
        setIsUpsellOpen(false);
        if (!!res.data.status) {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Upsell created successfully!",
            severity: "success"
          });

          pollForUpdate();
        } else {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Failed to create upsell.",
            severity: "error"
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          autoHideDuration: 2000,
          message: "Failed to create upsell.",
          severity: "error"
        });
      })
      .finally(() => {
        setIsUpsellOpen(false);
        setLoading(false);
      });
  };
  const handleUpsellUpdate = (id, payload) => {
    setLoading(true);
    upsellApi
      .updateUpsell(jwt, id, payload)
      .then(res => {
        console.log("Update message", res.data.message);
        setIsUpsellOpen(false);
        if (!!res.data.status) {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Upsell updated successfully!",
            severity: "success"
          });
          pollForUpdate();
        } else {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Failed to edit upsell.",
            severity: "error"
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          autoHideDuration: 2000,
          message: "Failed to edit upsell.",
          severity: "error"
        });
      })
      .finally(() => {
        setIsUpsellOpen(false);
        setLoading(false);
      });
  };

  const handleUpsellDelete = payload => {
    setLoading(true);

    upsellApi
      .deleteUpsell(jwt, payload.id)
      .then(res => {
        console.log(res);
        setDeleteModalOpen(false);
        if (!!res.data.status) {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Upsell deleted successfully!",
            severity: "success"
          });

          pollForUpdate();
        } else {
          setSnackbar({
            open: true,
            autoHideDuration: 2000,
            message: res.data.message || "Failed to delete upsell.",
            severity: "error"
          });
        }
      })
      .catch(() => {
        setSnackbar({
          open: true,
          autoHideDuration: 2000,
          message: "Failed to delete upsell.",
          severity: "error"
        });
      })
      .finally(() => {
        setLoading(false);
        setDeleteModalOpen(false);
      });
  };

  return (
    <div>
      <>
        <Grid container alignItems="center" justify="space-between">
          <div
            className="upsell-date-selector-container"
            style={{
              display: "flex",
              width: "100%",
              gap: "10px",
              marginBottom: "10px"
            }}
          >
            <Grid item xs={3}>
              <DateSelector
                value={analyticsStartDate}
                onChange={e => {
                  setAnalyticsStartDate(moment(e).format("YYYY-MM-DD"));
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <DateSelector
                value={analyticsEndDate}
                onChange={e => {
                  setAnalyticsEndDate(moment(e).format("YYYY-MM-DD"));
                }}
              />
            </Grid>
          </div>
          <div
            style={{
              display: "block",
              width: "100%"
            }}
          >
            <DashboardTable
              data={[
                {
                  cardName: "TOTAL UPSELLS COUNT",
                  cardValue: `${analyticsData.totalUpsells}` || "--"
                },
                {
                  cardName: "TOTAL REVENUE",
                  cardValue: `₦ ${analyticsData.totalRevenue}` || "--"
                },
                {
                  cardName: "CONVERSION RATE",
                  cardValue: `${analyticsData.conversionRate}%` || "--"
                }
              ]}
              loadingTable={analyticsLoading}
              tableTitle="Overview"
              filterComponent={
                <div>
                  <CSVLink data={data} filename={"upsell.csv"}>
                    <Button variant="contained" color="primary">
                      <DownloadIcon className="c-user-icon" /> DOWNLOAD CSV
                    </Button>
                  </CSVLink>
                </div>
              }
            />
          </div>

          <div
            style={{
              display: "block",
              width: "100%"
            }}
          >
            <Grid item xs={6}>
              {cinemasLoading ? (
                <CircularLoad show={true} />
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <TextField
                      id="select-change-url"
                      select
                      variant="outlined"
                      margin="normal"
                      label="Select state"
                      name="Select-State"
                      value={selectedState}
                      onChange={e => {
                        setSelectedState(e.target.value);
                        setSelectedCinema(e.target.value.cinemas[0]);
                      }}
                      fullWidth
                    >
                      {states.map(item => (
                        <MenuItem key={item?.id} value={item}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      id="select-change-url"
                      select
                      variant="outlined"
                      margin="normal"
                      label="Select cinema"
                      name="Select-Cinema"
                      value={selectedCinema}
                      onChange={e => setSelectedCinema(e.target.value)}
                      fullWidth
                    >
                      {selectedState.cinemas &&
                        selectedState?.cinemas.map(item => (
                          <MenuItem key={item?.id} value={item}>
                            {item?.cinema_name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </div>
          <Grid item xs={6}>
            <PaperedInput
              onChange={e => {
                e.persist();
                debounce(() => setSearchInput(e.target.value), 2000);
              }}
              placeholder="Search for Upsell"
            />
          </Grid>
          <Grid item xs={6}>
            <Grid justify="flex-end" container>
              <Button
                style={{ marginRight: "18px", height: "100%" }}
                onClick={() => {
                  setUpsellMode("create");
                  setIsUpsellOpen(true);
                }}
                variant="outlined"
                color="primary"
              >
                + CREATE UPSELL{" "}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <div style={{ height: "90vh", width: "100%", marginTop: "20px" }}>
          {error.isError ? (
            <Alert severity="error">{error.message}</Alert>
          ) : (
            <DataGrid
              rows={data}
              columns={columns}
              pageSize={10}
              rowHeight={72}
              headerHeight={60}
              disableColumnMenu={true}
              autoHeight={true}
              loading={loading}
              rowCount={totalData}
              onCellClick={params => {
                console.log(params);

                if (params.field === "") return;

                setSelectedUpsellProduct(params.row);
                setUpsellProductModalOpen(true);
              }}
              page={page}
              onPageChange={page => {
                setPage(page);
              }}
              paginationMode="server"
            />
          )}
        </div>
      </>
      <UpsellModal
        open={isUpsellOpen}
        onClose={() => setIsUpsellOpen(false)}
        onConfirm={(id, payload, mode) =>
          upsellMode === "edit"
            ? handleUpsellUpdate(id, payload)
            : handleUpsellConfirm(payload, mode)
        }
        upsellMode={upsellMode}
        upsellToEdit={upsellToEdit}
        cinemaId={selectedCinema.id}
      />

      <UpsellDeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleUpsellDelete}
        upsellToDelete={upsellToDelete}
      />

      <UpsellProductModal
        open={upsellProductModalOpen}
        onClose={() => {
          setUpsellProductModalOpen(false);
          setSelectedUpsellProduct(null);
        }}
        upsellDetails={selectedUpsellProduct}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <AlertSnackbar
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </AlertSnackbar>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = state => ({
  jwt: state.user.user.jwt
});

export default connect(mapStateToProps)(withRouter(UpsellManagement));
